import { Icon, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { ApplicationRoleApproverTypeEnum } from "../../../../models/ApplicationRoleApprover"
import { UtcDateTime } from "../../../../models/CustomType"
import {
  RoleRequestGet,
  RoleRequestPut,
  RoleRequestStatusEnum,
} from "../../../../models/RoleRequest"
import { copyToClipboard } from "../../../../utils/copyToClipboard"
import { getFutureDateTime } from "../../../../utils/getFormattedDate"

const validationSchema = z.object({
  period: z.number().min(1, "Period is required"),
})

export function RoleRequestDetails(props: {
  readonly roleRequest: RoleRequestGet
  readonly setRoleRequest: (updateRoleRequest: RoleRequestGet) => void
}) {
  const { roleRequest, setRoleRequest } = props

  const [expirationDate, setExpirationDate] = useState<UtcDateTime>()

  const formMethods = useForm<RoleRequestPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({ period: roleRequest.period })
    setExpirationDate(getFutureDateTime(roleRequest.period))
  }, [roleRequest.period, reset])

  const onSubmit = useCallback(
    async (values: RoleRequestPut) => {
      try {
        const res = await digitalManagerApi.put<RoleRequestGet>(
          `/roleRequests/${roleRequest.id}`,
          { period: values.period }
        )
        toast.success("Role request updated")
        setRoleRequest(res.data)
      } catch {
        toast.error("Cannot update role request")
        throw new Error("Cannot update role request")
      }
    },
    [roleRequest.id, setRoleRequest]
  )

  const period = useWatch({ control, name: "period" })

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<RoleRequestPut, "period">) => (value: string) => {
      const parsed = value ? Number.parseInt(value, 10) : ""
      field.onChange(parsed)
    },
    []
  )

  const renderPeriodTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<RoleRequestPut, "period"> }) => (
      <MUITextField
        id="period"
        name={field.name}
        label="Days"
        type="number"
        size={32}
        value={field.value?.toString() ?? ""}
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.period?.message === "string"
            ? { period: { message: errors.period.message } }
            : undefined
        }
      />
    ),
    [errors.period, handleParsedNumberChange]
  )

  useEffect(() => {
    if (period) {
      setExpirationDate(getFutureDateTime(period))
    }
  }, [period])

  const copyToClipboardReferentEmail = useCallback(() => {
    copyToClipboard(roleRequest.referentEmail)
  }, [roleRequest.referentEmail])

  const canWrite =
    roleRequest.status === RoleRequestStatusEnum.Open &&
    roleRequest.approvals.some(
      (a) =>
        a.userApproverType === ApplicationRoleApproverTypeEnum.Approver ||
        a.userApproverType === ApplicationRoleApproverTypeEnum.BackupApprover
    )

  const detailsContentProps: DiMaDetailsContentProps<RoleRequestPut> = {
    canWrite,
    formMethods,
    onSubmit,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Application name",
        displayItem: <div style={{ width: "100%" }}>{roleRequest.application.name}</div>,
      },
      {
        id: 1,
        label: "Role name",
        displayItem: <div style={{ width: "100%" }}>{roleRequest.role.name}</div>,
      },
      {
        id: 2,
        label: "Referent email",
        displayItem: (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ textTransform: "none" }}>{roleRequest.referentEmail}</div>
            <Icon
              iconName="icon_abb_dias icon-abb_copy-1"
              size={24}
              onClick={copyToClipboardReferentEmail}
            />
          </div>
        ),
      },
      {
        id: 3,
        label: "Reason",
        displayItem: <div style={{ width: "100%" }}>{roleRequest.reason}</div>,
      },
      {
        id: 4,
        label: "Period",
        displayItem: <div style={{ width: "100%" }}>{roleRequest.period} days</div>,
        editItem: (
          <Controller name="period" control={control} render={renderPeriodTextField} />
        ),
      },
      {
        id: 5,
        label: "Expiration Date",
        displayItem: <div style={{ width: "100%" }}>{expirationDate}</div>,
      },
      {
        id: 6,
        label: "Version",
        displayItem: <div style={{ width: "100%" }}>{roleRequest.version ?? "-"}</div>,
      },
      {
        id: 7,
        label: "Operating System",
        displayItem: (
          <div style={{ width: "100%" }}>{roleRequest.operatingSystem ?? "-"}</div>
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
